/**
 * Private Inbox, endpoint side. The hub lists ask ids and routing metadata for encrypted runs;
 * this module fetches each ask's ciphertext, decrypts it locally and turns it into the same
 * `InboxItem` the plaintext lane produced. Ship/build/landing cards are derived here from the
 * encrypted report readers; the hub only stores their snoozes under opaque ids.
 *
 * The service worker reuses `describeAsk`/`notificationText` (lib/notification-detail.ts).
 */
import type { DeviceAlertReport, DigestReport, PrivateNotification } from '@viko/protocol'
import type { Api } from './api.js'
import { type AskOpener, describeAsk, LOCKED_TITLE, openPrivateAsk } from './ask-view.js'
import { digestCardItems } from './digests.js'
import type { BuildCard, InboxItem, Landing } from './types.js'

export { type AskOpener, describeAsk, LOCKED_TITLE, openPrivateAsk }

export interface PrivateInboxDeps {
  notification(id: string): Promise<PrivateNotification>
  open: AskOpener
}

/** Fill in every private ask; one that cannot be opened stays visible but locked (no text). */
export async function resolvePrivateItems(
  items: InboxItem[],
  deps: PrivateInboxDeps,
): Promise<InboxItem[]> {
  return Promise.all(
    items.map(async (item) => {
      if (!item.private) return item
      const hubId = item.itemId ?? item.id
      try {
        const n = await deps.notification(hubId)
        const event = await openPrivateAsk(n, deps.open)
        const { title } = describeAsk(event)
        return {
          ...item,
          id: event.kind === 'question' ? event.questionId : event.approvalId,
          itemId: hubId,
          title,
          event,
          blocking: event.kind === 'question' ? event.blocking : true,
        }
      } catch {
        return { ...item, itemId: hubId, locked: true, title: LOCKED_TITLE }
      }
    }),
  )
}

// ---- ship / build / landing cards derived from encrypted reports ------------------------------

const DAY = 86_400_000
export const buildCardId = (b: Pick<BuildCard, 'buildId'>) => `b:${b.buildId}`
export const landingCardId = (l: Pick<Landing, 'id'>) => `l:${l.id}`
export const alertCardId = (a: Pick<DeviceAlertReport, 'alertId'>) => `a:${a.alertId}`

/**
 * Failed builds block; a finished build is a ship card for a day; a landing awaiting approval is
 * a landing card. Snoozed ones are hidden until their time (hub-stored, opaque ids).
 */
export function deriveCardItems(
  input: { builds: BuildCard[]; landings: Landing[]; alerts?: DeviceAlertReport[] },
  snoozes: Record<string, number>,
  now: number,
): InboxItem[] {
  const hidden = (id: string) => (snoozes[id] ?? 0) > now
  const out: InboxItem[] = []
  for (const b of input.builds) {
    const id = buildCardId(b)
    const at = b.updatedAt ?? b.createdAt ?? now
    const failed = b.status === 'failed'
    // A later successful build of the same lane resolves the failure.
    if (
      failed &&
      input.builds.some(
        (o) =>
          o.status === 'available' &&
          o.projectId === b.projectId &&
          o.laneId === b.laneId &&
          (o.updatedAt ?? o.createdAt ?? 0) > at,
      )
    )
      continue
    const shippable = b.status === 'available' && now - at < DAY
    if ((!failed && !shippable) || hidden(id)) continue
    out.push({
      id,
      itemId: id,
      runId: '',
      type: 'build',
      kind: 'build',
      seq: 0,
      title: b.title,
      blocking: failed,
      createdAt: at,
      score: 0,
      body: b,
      derived: true,
    })
  }
  // Landing is async and quiet: a card only when the newest landing of a workstream failed.
  const newest = new Map<string, Landing>()
  for (const l of input.landings) {
    const k = l.workstreamId ?? l.branch ?? l.id
    const cur = newest.get(k)
    if (!cur || l.updatedAt > cur.updatedAt) newest.set(k, l)
  }
  for (const l of newest.values()) {
    const id = landingCardId(l)
    if (l.state !== 'failed' || now - l.updatedAt > DAY || hidden(id)) continue
    // A later successful landing of the same branch or HEAD resolves it, whatever the workstream.
    if (
      input.landings.some(
        (o) =>
          o.state === 'landed' &&
          o.updatedAt > l.updatedAt &&
          ((l.branch && o.branch === l.branch) || (l.headSha && o.headSha === l.headSha)),
      )
    )
      continue
    const reason = l.reason ?? l.error ?? 'landing failed'
    out.push({
      id,
      itemId: id,
      runId: l.runId ?? '',
      type: 'card',
      kind: 'blocked',
      category: 'landing-failed',
      seq: 0,
      // g18-stack: a plan branch (`plan:<id>`) is named by its plan ("Plan: <title> — wave 2 of 6").
      title: `Landing blocked: ${(l.workstreamId?.startsWith('plan:') ? l.summary : undefined) ?? l.branch ?? l.summary ?? 'branch'} — ${reason}`,
      blocking: false,
      createdAt: l.updatedAt,
      score: 0,
      ...(l.workstreamId ? { workstreamId: l.workstreamId } : {}),
      ...(l.machineId ? { machineId: l.machineId } : {}),
      body: {
        landingId: l.id,
        error: reason,
        ...(l.baselineFailing ? { baselineFailing: true } : {}),
      },
      derived: true,
    })
  }
  // ---- g2g3-alerts: battery / disk / usage-limit cards; critical ones block until cleared ----
  for (const a of input.alerts ?? []) {
    const id = alertCardId(a)
    if (a.resolvedAt || hidden(id)) continue
    out.push({
      id,
      itemId: id,
      runId: a.runId ?? '', // machine-resources: `wait` cards open their run
      type: 'card',
      kind: 'blocked',
      category: 'device-alert',
      seq: 0,
      title: a.title,
      blocking: a.level === 'critical',
      createdAt: a.raisedAt,
      score: 0,
      body: a,
      derived: true,
    })
  }
  // ---- end g2g3-alerts ----
  for (const l of input.landings) {
    const id = landingCardId(l)
    if (l.state !== 'awaiting_approval' || !l.workstreamId || hidden(id)) continue
    out.push({
      id,
      itemId: id,
      runId: l.runId ?? '',
      type: 'land',
      kind: 'landing',
      seq: 0,
      title: l.summary ?? l.branch ?? 'Ready to land',
      blocking: true,
      createdAt: l.updatedAt,
      score: 0,
      workstreamId: l.workstreamId,
      ...(l.machineId ? { machineId: l.machineId } : {}),
      land: {
        workstreamId: l.workstreamId,
        branch: l.branch ?? '',
        commits: 0,
        files: 0,
        additions: 0,
        deletions: 0,
        tasks: [],
        ...(l.headSha ? { headSha: l.headSha } : {}),
      },
      derived: true,
    })
  }
  return out
}

/**
 * Wrap `api.inbox` so encrypted asks are decrypted here and report-derived cards join the list.
 * Reports being locked or unavailable never hides the asks (and vice versa).
 */
export function installPrivateInbox(
  api: Api,
  o: {
    open: AskOpener
    /** False before the account is unlocked: asks stay locked, derived cards are skipped. */
    ready: () => Promise<boolean>
    now?: () => number
  },
) {
  const hub = api.inbox
  const now = o.now ?? Date.now
  api.inbox = async () => {
    const items = await hub()
    const asks = await resolvePrivateItems(items, {
      notification: (id) => api.privateNotification(id),
      open: async (e) => {
        if (!(await o.ready())) throw new Error('locked')
        return o.open(e)
      },
    })
    let cards: InboxItem[] = []
    try {
      if (await o.ready()) {
        const [builds, landings, snoozes, alerts] = await Promise.all([
          api.builds(),
          api.landings(),
          api.cardSnoozes().catch(() => ({}) as Record<string, number>),
          api.deviceAlerts().catch(() => [] as DeviceAlertReport[]),
        ])
        cards = deriveCardItems({ builds, landings, alerts }, snoozes, now())
        // ---- g13-digests: recent digests as FYI cards (decrypted reports only) ----
        const digests = await (api.digests?.() ?? Promise.resolve([])).catch(
          () => [] as DigestReport[],
        )
        cards = [...cards, ...digestCardItems(digests, snoozes, now())]
        // ---- end g13-digests ----
      }
    } catch {
      // Reports unavailable: show asks only.
    }
    return [...asks, ...cards]
  }
}

// ---- answering exactly once ----------------------------------------------------------------------

export type AnswerResult = 'sent' | 'already-answered'

/**
 * Claim the ask with a fresh command id (first writer wins on the hub), then deliver the answer
 * with that id. A repeat tap, a second device or a retry after a lost response can never deliver a
 * second answer: the loser gets `already-answered`, the winner's own retry reuses its claimed id.
 */
export function answerOnce(
  d: {
    claim(itemId: string, commandId: string): Promise<{ won: boolean }>
    send(commandId: string): Promise<unknown>
    isDuplicate?(e: unknown): boolean
  },
  ids: Map<string, string>,
  itemId: string,
) {
  return async (): Promise<AnswerResult> => {
    const commandId = ids.get(itemId) ?? crypto.randomUUID()
    ids.set(itemId, commandId)
    if (!(await d.claim(itemId, commandId)).won) return 'already-answered'
    try {
      await d.send(commandId)
    } catch (e) {
      // The hub already holds a command with this id: the first attempt got through.
      if (d.isDuplicate?.(e)) return 'sent'
      throw e
    }
    return 'sent'
  }
}
